/**
 * PdfPreviewPanel — Shared right-side slide-in panel for PDF preview.
 *
 * Fetches PDF bytes from /api/v1/court-forms/preview-pdf/:id and renders
 * via PdfViewer in a portal-based panel with backdrop, escape-to-close,
 * and retry on error.
 *
 * Used by: TemplateDetailForms, CourtReadinessRow, and any future PDF preview surfaces.
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DocumentIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { PdfViewer } from './PdfViewer';
import { WeBelieveLogger } from '@/utils/WeBelieveLogger';
import { useKeycloak } from '@react-keycloak/web';

interface PdfPreviewPanelProps {
  /** The court_forms.id used to fetch /api/v1/court-forms/preview-pdf/:id */
  courtFormId: string;
  /** Display label shown in the panel header */
  label: string;
  /** Called when the user closes the panel (backdrop click, X button, or Escape) */
  onClose: () => void;
  /** Logging source tag — identifies the calling component */
  source?: string;
}

export function PdfPreviewPanel({
  courtFormId,
  label,
  onClose,
  source = 'PdfPreviewPanel',
}: PdfPreviewPanelProps) {
  const [pdfData, setPdfData] = useState<Uint8Array | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { keycloak } = useKeycloak();

  const fetchPdf = useCallback(async () => {
    setPdfData(null);
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(
        `/api/v1/court-forms/preview-pdf/${courtFormId}`,
        {
          headers: keycloak?.token
            ? { Authorization: `Bearer ${keycloak.token}` }
            : {},
        },
      );

      if (!response.ok) {
        setError(`Failed to load PDF (${response.status})`);
        WeBelieveLogger.warn(source, 'PDF preview fetch failed', {
          courtFormId,
          status: response.status,
        });
        return;
      }

      const arrayBuffer = await response.arrayBuffer();
      setPdfData(new Uint8Array(arrayBuffer));
      WeBelieveLogger.info(source, 'PDF preview opened', {
        courtFormId,
        label,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load PDF');
    } finally {
      setLoading(false);
    }
  }, [courtFormId, keycloak?.token, label, source]);

  // Fetch on mount
  useEffect(() => {
    fetchPdf();
  }, [fetchPdf]);

  // Escape key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Download handler
  const handleDownload = useCallback(() => {
    if (!pdfData) return;
    const blob = new Blob([pdfData], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${label.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    WeBelieveLogger.info(source, 'PDF downloaded', { courtFormId, label });
  }, [pdfData, label, courtFormId, source]);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[70] bg-black/30 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 z-[71] h-full w-[60vw] max-w-3xl bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col"
        style={{ animation: 'lex-panel-slide-in 200ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <div className="flex items-center gap-2 min-w-0">
            <DocumentIcon className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {label}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {pdfData && (
              <button
                onClick={handleDownload}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors"
                title="Download PDF"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto p-4">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <p className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
              <button
                onClick={fetchPdf}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {pdfData && <PdfViewer data={pdfData} />}
        </div>
      </div>
    </>,
    document.body,
  );
}
